import { Modal, Radio, Typography, Checkbox, Select, message } from 'antd';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useState } from 'react';
import slugify from 'slugify';

import { getPropertyValidations } from '@marketreach/utils/properties';

import PropertySettings from '../PropertySettings';
import PropertyValidation from '../PropertyValidation';

import '../styles.scss';

const { Option } = Select;
const { Text } = Typography;

const initialState = {
  section: null,
  settings: {
    key: '',
    label: '',
    defaultValue: null,
    description: '',
    fieldOptions: {
      allowMultipleValues: false,
      localizeField: false,
    },
  },
  validation: {
    required: false,
    unique: false,
    limit_characters: null,
    match_pattern: null,
    restrict_pattern: null,
    range: null,
    file_count_limit: null,
    file_size_limit: null,
  },
  advanced: {
    hideField: false,
  },
  propertyType: {
    title: '',
    image: '',
    description: '',
  },
};

const AddNewPropertyModal = (props) => {
  const {
    sections,
    type,
    open,
    handleOk,
    handleCancel,
    data,
    entityType,
    apiIdField,
  } = props;
  const [state, setState] = useState({ ...initialState, propertyType: type });
  const [error, setError] = useState({
    key: false,
    label: false,
  });

  const handleError = (field, value) => {
    setError({
      ...error,
      [field]: value,
    });
  };
  const handleChangeState = (part, field, value, entityType) => {
    if (field === 'key' || field === 'label') {
      handleError(field, !value);
    }

    const newState = {
      ...state,
      [part]: {
        ...state[part],
        [field]: value,
      },
    };

    if (entityType) {
      newState[part][`${field}Type`] = entityType;
    }

    if (field === 'label') {
      // if label was changed - update apiID
      newState[part].key = slugify(value);
    }

    setState(newState);
  };

  const handleSelectSection = (value) => {
    handleError('section', false);
    const section = sections.find((sec) => sec.order === value);

    setState({
      ...state,
      // remove recursive include of settings properties
      section: { ...section, properties: [] },
    });
  };

  useEffect(() => {
    if (open) {
      setState({ ...initialState, propertyType: type });
    }
  }, [open]);

  const [part, setPart] = useState('settings');
  const handleChangePart = (e) => {
    setPart(e.target.value);
  };

  const onOk = () => {
    setError({
      key: !state.settings.key,
      label: !state.settings.label,
      section: !state.section,
    });

    if (!state.settings.key || !state.settings.label || !state.section) {
      message.error('Please input required fields');
      return;
    }

    handleOk(state);
  };

  const onCancel = () => {
    handleCancel();
  };

  const validations = getPropertyValidations(type?.title);

  return (
    <Modal
      className="taxonomy-add-property-modal"
      title="Add Property"
      visible={open}
      onOk={onOk}
      closeIcon={
        type && (
          <div className="custom-close-x">
            {type?.title}
            <img src={type?.image} alt={type?.title} />
          </div>
        )
      }
      onCancel={onCancel}
      width={700}
    >
      <div className="section-select">
        <Select
          showSearch
          style={{
            width: 200,
            marginRight: 12,
          }}
          placeholder="Select section"
          optionFilterProp="children"
          value={state?.section?.order}
          onChange={handleSelectSection}
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {sections.length > 0 &&
            _.orderBy(sections, 'name').map((section) => (
              <Option key={section.order} value={section.order}>
                {section.name}
              </Option>
            ))}
        </Select>
        {error?.section && <Text type="danger">Section is required.</Text>}
      </div>
      <Radio.Group
        className="taxonomy-add-property-modal-radio"
        value={part}
        onChange={handleChangePart}
      >
        <Radio.Button value="settings">Settings</Radio.Button>
        <Radio.Button value="validation">Validation</Radio.Button>
        <Radio.Button value="advanced">Advanced</Radio.Button>
      </Radio.Group>
      {part === 'settings' && (
        <>
          <PropertySettings
            type={type}
            state={state}
            error={error}
            handleChangeState={handleChangeState}
            data={data}
            entityType={entityType}
            apiIdField={apiIdField}
          />
        </>
      )}
      {part === 'validation' && (
        <>
          {validations.length > 0 &&
            validations.map((validation, index) => (
              <PropertyValidation
                key={`validation_${index}`}
                type={validation}
                state={state}
                handleChangeState={handleChangeState}
              />
            ))}
        </>
      )}
      {part === 'advanced' && (
        <Checkbox
          className="taxonomy-add-property-modal-item"
          checked={state.advanced.hideField}
          onChange={(e) =>
            handleChangeState('advanced', 'hideField', e.target.checked)
          }
        >
          <Typography className="taxonomy-add-property-modal-item-title">
            Hide field
          </Typography>
          <Typography>
            This field will only be available in the API, an not visible to
            content editors.
          </Typography>
        </Checkbox>
      )}
    </Modal>
  );
};

AddNewPropertyModal.propTypes = {
  sections: PropTypes.array,
  type: PropTypes.object,
  open: PropTypes.bool,
  data: PropTypes.object,
  handleOk: PropTypes.func,
  handleCancel: PropTypes.func,
  entityType: PropTypes.string,
  apiIdField: PropTypes.string,
};

AddNewPropertyModal.defaultProps = {
  sections: [],
  type: null,
  open: false,
  data: null,
  handleOk: () => {},
  handleCancel: () => {},
  entityType: null,
  apiIdField: null,
};

export default memo(AddNewPropertyModal);
